import {
  isAppearance,
  isSafeCustomCss,
  resolveNoticeAppearance,
  withColorScheme,
  type NoticeAppearance,
  type NoticeAppearanceColors,
  type ResolvedNoticeAppearance,
} from "../shared/appearance";
import type { Settings, SettingsPalette } from "../api/types";

/** Drops unset keys, so a host value only replaces a console value it names. */
const definedColors = (
  colors: Record<string, string | undefined>,
): NoticeAppearanceColors =>
  Object.fromEntries(
    Object.entries(colors).filter(([, value]) => Boolean(value)),
  ) as NoticeAppearanceColors;

/** A host palette in the console's colour keys; unset colours are dropped. */
const hostColors = (
  palette: Partial<SettingsPalette> | undefined,
): NoticeAppearanceColors => {
  if (!palette) {
    return {};
  }
  const button = palette.button;
  return definedColors({
    background: palette.backgroundColor,
    surface: palette.surfaceColor,
    heading: palette.headingColor,
    text: palette.textColor,
    muted_text: palette.mutedTextColor,
    link: palette.link,
    border: palette.borderColor,
    overlay: palette.overlayColor,
    // `||`, not `??`: an empty string is unset, as it always was.
    accept_all_bg:
      button?.acceptAll?.backgroundColor || button?.accept?.backgroundColor,
    accept_all_text: button?.acceptAll?.textColor || button?.accept?.textColor,
    accept_bg: button?.acceptSelected?.backgroundColor,
    accept_text: button?.acceptSelected?.textColor,
    decline_bg: button?.decline?.backgroundColor,
    decline_text: button?.decline?.textColor,
    decline_border: button?.decline?.borderColor,
    language_bg: button?.language?.backgroundColor,
    language_text: button?.language?.textColor,
    language_selected_bg: button?.language?.selectedBackgroundColor,
    language_selected_text: button?.language?.selectedTextColor,
    toggle_on: palette.toggle?.onColor || button?.accept?.backgroundColor,
    toggle_off: palette.toggle?.offColor,
    toggle_knob: palette.toggle?.knobColor,
    tts_highlight_bg: palette.ttsHighlight?.backgroundColor,
    tts_highlight_text: palette.ttsHighlight?.textColor,
  });
};

/**
 * Host `settings` over the console appearance, key by key: whatever the
 * integrator sets in code wins, and everything they leave unset keeps the
 * console's value (which in turn falls back to the notice's brand colours).
 * `settings.darkPalette` merges over the console's dark palette the same way.
 *
 * `button.accept` keeps the meaning it has always had: the brand accent, and
 * Accept All's colour when `acceptAll` is unset.
 */
export const mergeHostSettings = (
  resolved: ResolvedNoticeAppearance,
  settings: Partial<Settings> | undefined,
): ResolvedNoticeAppearance => {
  if (!settings) {
    return resolved;
  }
  const hostCss = settings.customCss;
  return {
    // An unrecognised host style is ignored rather than forcing classic.
    style: isAppearance(settings.theme) ? settings.theme : resolved.style,
    colors: { ...resolved.colors, ...hostColors(settings) },
    radius: settings.borderRadius || resolved.radius,
    selectionControl: settings.selectionControl ?? resolved.selectionControl,
    controlStyle: settings.controlStyle ?? resolved.controlStyle,
    confirmBeforeSubmit:
      settings.confirmBeforeSubmit ?? resolved.confirmBeforeSubmit,
    mobileLayout: settings.mobileLayout ?? resolved.mobileLayout,
    desktopPosition: settings.desktopPosition ?? resolved.desktopPosition,
    backdrop: settings.backdrop ?? resolved.backdrop,
    collapsibleSections:
      settings.collapsibleSections ?? resolved.collapsibleSections,
    phoneFooter: settings.phoneFooter ?? resolved.phoneFooter,
    autoDark: settings.autoDark ?? resolved.autoDark,
    darkColors: {
      ...resolved.darkColors,
      ...hostColors(settings.darkPalette),
    },
    maxWidth: settings.maxWidth || resolved.maxWidth,
    logoSize: settings.logoSize || resolved.logoSize,
    textScale: settings.textScale ?? resolved.textScale,
    motion: settings.motion ?? resolved.motion,
    // Host CSS passes the same guard as the console's; refused, the
    // console's still applies.
    customCss:
      hostCss && isSafeCustomCss(hostCss) ? hostCss : resolved.customCss,
  };
};

/**
 * The served appearance under the host's `settings`, painted with the dark
 * palette while the device prefers dark and the appearance asks for
 * automatic dark mode.
 */
export const resolveNoticeLook = (
  served: NoticeAppearance | null | undefined,
  settings: Partial<Settings> | undefined,
  prefersDark: boolean,
): ResolvedNoticeAppearance =>
  withColorScheme(
    mergeHostSettings(resolveNoticeAppearance(served), settings),
    prefersDark,
  );
